const STORAGE_KEY = 'grapesjs-cross-tab-clipboard';
const STORAGE_TIMESTAMP_KEY = 'grapesjs-cross-tab-clipboard-timestamp';

const generateUniqueId = () => {
    return 'gjs-' + Math.random().toString(36).substr(2, 9);
};

const collectComponentIds = (component, idSet = new Set()) => {
    if (!component) return idSet;
    const id = component?.getId();
    if (id) idSet.add(id);
    if (component.components && typeof component.components === 'function') {
    const children = component.components();
    if (children && children.length) {
      children.forEach(child => collectComponentIds(child, idSet));
    }
  }

    return idSet;
};

const getStylesForIds = (editor, ids) => {
    const cssComposer = editor.CssComposer;
    const deviceManager = editor.DeviceManager;
    const allStyles = [];

    const devices = deviceManager ? deviceManager.getAll() : [];
    const currentDevice = deviceManager ? deviceManager.getSelected() : null;

    const getRulesForDevice = (device) => {
        // Temporarily switch to the device if needed to get its rules
        if (device && deviceManager) {
            deviceManager.select(device);
        }

        const rules = cssComposer.getAll();
        return rules.filter(rule => {
            const selector = rule.getSelectorsString ? rule.getSelectorsString() : '';
            return selector && ids.some(id => selector.includes(`#${id}`));
        }).map(rule => {
            let atRule = '';
            let atRuleType = '';
            let atRuleParams = '';

            if (rule.getAtRule && typeof rule.getAtRule === 'function') {
                atRule = rule.getAtRule() || '';
            } else if (rule.get('atRule')) {
                atRule = rule.get('atRule') || '';
            }

            if (!atRule && rule.get && rule.get('mediaText')) {
                atRule = rule.get('mediaText');
            }

            if (atRule) {
                const match = atRule.match(/@(\w+)\s*(.*)/);
                if (match) {
                    atRuleType = match[1];
                    atRuleParams = match[2] || '';
                }
            }

            const deviceName = device ? (device.get ? device.get('name') : device.name) : '';
            const deviceWidth = device ? (device.get ? device.get('width') : device.width) : '';

            return {
                selectors: rule.getSelectors ? rule.getSelectors() : [],
                style: rule.getStyle ? rule.getStyle() : {},
                mediaText: atRule,
                atRuleType: atRuleType,
                atRuleParams: atRuleParams,
                deviceName: deviceName,
                deviceWidth: deviceWidth,
            };
        });
    };

    if (devices && devices.length > 0) {
        devices.forEach(device => {
            const deviceRules = getRulesForDevice(device);
            allStyles.push(...deviceRules);
        });
    }

    const defaultRules = cssComposer.getAll().filter(rule => {
        const selector = rule.getSelectorsString ? rule.getSelectorsString() : '';
        return selector && ids.some(id => selector.includes(`#${id}`));
    }).map(rule => {
        let atRule = '';
        let atRuleType = '';
        let atRuleParams = '';
        if (rule.getAtRule && typeof rule.getAtRule === 'function') {
            atRule = rule.getAtRule() || '';
        } else if (rule.get('atRule')) {
            atRule = rule.get('atRule') || '';
        }

        if (!atRule && rule.get && rule.get('mediaText')) {
            atRule = rule.get('mediaText');
        }

        if (atRule) {
            const match = atRule.match(/@(\w+)\s*(.*)/);
            if (match) {
                atRuleType = match[1];
                atRuleParams = match[2] || '';
            }
        }
        return {
            selectors: rule.getSelectors ? rule.getSelectors() : [],
            style: rule.getStyle ? rule.getStyle() : {},
            mediaText: atRule,
            atRuleType: atRuleType,
            atRuleParams: atRuleParams,
        };
    });

    // Merge and deduplicate
    const styleMap = new Map();
    [...allStyles, ...defaultRules].forEach(style => {
        const key = JSON.stringify({
            selectors: style.selectors,
            atRuleType: style.atRuleType,
            atRuleParams: style.atRuleParams,
        });
        if (!styleMap.has(key)) {
            styleMap.set(key, style);
        }
    });

    // Restore original device selection
    if (currentDevice && deviceManager) {
        deviceManager.select(currentDevice);
    }

    return Array.from(styleMap.values());
};

const updateComponentIdsAndMap = (component, idMap = {}) => {
    if (component.attributes && component.attributes.id) {
        const oldId = component.attributes.id;
        const newId = generateUniqueId();
        idMap[oldId] = newId;
        component.attributes.id = newId;
    }
    if (component.components && Array.isArray(component.components)) {
        component.components.forEach(child => updateComponentIdsAndMap(child, idMap));
    }
    return idMap;
};

const updateStylesWithNewIds = (styles, idMap) => {
    if (!Array.isArray(styles)) {
        return styles;
    }

    return styles.map((styleRule) => {
        let updatedRule = { ...styleRule };
        if (updatedRule.selectors && Array.isArray(updatedRule.selectors)) {
            updatedRule.selectors = updatedRule.selectors.map((selector) => {
                let newSelector = selector;
                let wasUpdated = false;
                for (const oldId in idMap) {
                    const newId = idMap[oldId];
                    const regex = new RegExp(`#${oldId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
                    const originalSelector = newSelector;
                    newSelector =
                        typeof newSelector === 'object'
                            ? newSelector
                            : newSelector?.replace(regex, `#${newId}`);

                    if (originalSelector !== newSelector) {
                        wasUpdated = true;
                    }
                }

                return newSelector;
            });
        }

        return updatedRule;
    });
};

export const crossTabCopyPaste = (editor) => {
    const { Commands, Panels } = editor;
    Commands.add('cross-tab-copy', {
        run: (editor, sender) => {
            const selected = editor.getSelected();

            if (!selected) {
                editor.Modal.open({
                    title: 'No Component Selected',
                    content: 'Please select a component to copy.',
                    attributes: { class: 'gjs-modal-sm' },
                });
                return;
            }

            try {
                const componentData = selected.toJSON();
                const componentIds = [...collectComponentIds(selected)];
                const relatedStyles = getStylesForIds(editor, componentIds);
                const clipboardData = {
                    component: componentData,
                    styles: relatedStyles,
                };
                localStorage.setItem(STORAGE_KEY, JSON.stringify(clipboardData));
                localStorage.setItem(STORAGE_TIMESTAMP_KEY, Date.now().toString());

                // const pasteBtn = Panels.getButton('options', 'cross-tab-paste');
                // if (pasteBtn) {
                //     pasteBtn.set('active', true);
                // }

                editor.Modal.open({
                    title: 'Component Copied',
                    content: 'Component has been copied to clipboard. You can now paste it in another tab.',
                    attributes: { class: 'gjs-modal-sm' },
                });


            } catch (error) {
                console.error('Error copying component:', error);
                editor.Modal.open({
                    title: 'Copy Failed',
                    content: 'Failed to copy component. Please try again.',
                    attributes: { class: 'gjs-modal-sm' },
                });
            }
        },
    });

    Commands.add('cross-tab-paste', {
        run: (editor, sender) => {
            try {
                const clipboardData = localStorage.getItem(STORAGE_KEY);

                if (!clipboardData) {
                    editor.Modal.open({
                        title: 'Clipboard Empty',
                        content: 'No component found in clipboard. Copy a component first.',
                        attributes: { class: 'gjs-modal-sm' },
                    });
                    return;
                }

                const clipboardDataParsed = JSON.parse(clipboardData);

                let componentData, stylesToAdd;
                if (clipboardDataParsed.component) {
                    componentData = clipboardDataParsed.component;
                    stylesToAdd = clipboardDataParsed.styles || [];
                } else {
                    componentData = clipboardDataParsed;
                    stylesToAdd = [];
                }

                if (!componentData || typeof componentData !== 'object') {
                    throw new Error('Invalid component data structure');
                }

                const clonedComponent = JSON.parse(JSON.stringify(componentData));
                const clonedStyles = JSON.parse(JSON.stringify(stylesToAdd));

                const idMap = updateComponentIdsAndMap(clonedComponent);

                const updatedStyles = updateStylesWithNewIds(clonedStyles, idMap);

                const selected = editor.getSelected();
                let target = selected;

                if (!target || !target.get('droppable')) {
                    target = editor.getWrapper();
                }
                const targetComponents = target.components();
                const newComponent = targetComponents.add(clonedComponent);
                const ensureComponentIds = (component, expectedIdMap) => {
                    if (!component) return;
                    const currentId = component.get('id') || component.getAttributes()?.id;
                    if (currentId) {
                        if (!component.get('id') && component.getAttributes()?.id) {
                            component.set('id', component.getAttributes().id);
                        }
                    }
                    if (component.components && component.components().length > 0) {
                        component.components().each(child => {
                            ensureComponentIds(child, expectedIdMap);
                        });
                    }
                };
                ensureComponentIds(newComponent, idMap);
                if (updatedStyles && updatedStyles.length > 0) {
                    const cssComposer = editor.CssComposer;
                    const currentRules = cssComposer.getAll();

                    updatedStyles.forEach(newStyleRule => {
                        if (!newStyleRule.selectors || !Array.isArray(newStyleRule.selectors)) {
                            return;
                        }
                        const newSelector = newStyleRule.selectors.join(', ');

                        // Check if rule with same selector and media query already exists
                        const exists = currentRules.some(existingRule => {
                            const existingSelector = existingRule.getSelectorsString();
                            const existingAtRule = existingRule.getAtRule ? existingRule.getAtRule() : '';
                            const newAtRule = newStyleRule.mediaText || '';

                            return existingSelector === newSelector && existingAtRule === newAtRule;
                        });

                        if (!exists) {
                            try {
                                const ruleOptions = {};

                                if (newStyleRule.atRuleType && newStyleRule.atRuleParams) {
                                    ruleOptions.atRuleType = newStyleRule.atRuleType;
                                    ruleOptions.atRuleParams = newStyleRule.atRuleParams;
                                } else if (newStyleRule.mediaText) {
                                    const match = newStyleRule.mediaText.match(/@(\w+)\s*(.*)/);
                                    if (match) {
                                        ruleOptions.atRuleType = match[1];
                                        ruleOptions.atRuleParams = match[2] || '';
                                    }
                                }

                                const rule = cssComposer.setRule(
                                    newSelector,
                                    newStyleRule.style || {},
                                    ruleOptions
                                );
                                console.log('Added style rule:', newSelector, ruleOptions.atRuleParams ? `(${ruleOptions.atRuleParams})` : '');
                            } catch (error) {
                                console.error('Error adding style rule:', error, newStyleRule);
                            }
                        } else {
                            console.log('Style rule already exists, skipping:', newSelector, newStyleRule.atRuleParams ? `(${newStyleRule.atRuleParams})` : '');
                        }
                    });
                    editor.refresh();
                } else {
                    console.log('No styles to add');
                }
                editor.select(newComponent);
                editor.Modal.open({
                    title: 'Component Pasted',
                    content: 'Component has been pasted successfully.',
                    attributes: { class: 'gjs-modal-sm' },
                });
                setTimeout(() => {
                    editor.Modal.close();
                }, 2000);
            } catch (error) {
                console.error('Error pasting component:', error);
                editor.Modal.open({
                    title: 'Paste Failed',
                    content: `Failed to paste component: ${error.message || 'The clipboard data may be invalid.'}`,
                    attributes: { class: 'gjs-modal-sm' },
                });
            }
        },
    });

    const checkClipboard = () => {
        const clipboardData = localStorage.getItem(STORAGE_KEY);
        const pasteBtn = Panels.getButton('options', 'cross-tab-paste');

        if (pasteBtn) {
            if (clipboardData) {
                pasteBtn.set('active', true);
            } else {
                pasteBtn.set('active', false);
            }
        }
    };

    window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEY || e.key === STORAGE_TIMESTAMP_KEY) {
            // checkClipboard();
        }
    });

    editor.on('load', () => {
        setTimeout(() => {
            // checkClipboard();
        }, 100);
    });
};
